<template>
  <div class="bulk-dtr-page p-4">
    <div class="card shadow-sm border-0">
      <div class="card-body">
        <h5 class="font-weight-bold mb-1">Batch DTR Print</h5>
        <p class="text-muted mb-3">
          {{ typeLabel }} &middot; {{ from }} to {{ to }}
        </p>

        <div v-if="loading" class="text-muted">
          <span class="spinner-border spinner-border-sm mr-2"></span>
          Loading employees…
        </div>

        <div v-else-if="!batchData.length" class="text-muted">
          No active employees found for this group.
        </div>

        <div v-else>
          <p class="mb-2">
            <strong>{{ batchData.length }}</strong>
            {{ batchData.length === 1 ? "employee" : "employees" }} ready to print
            (one page each in a single print job).
          </p>
          <p v-if="statusMessage" class="text-primary mb-3">{{ statusMessage }}</p>

          <button
            type="button"
            class="btn btn-primary mr-2"
            :disabled="printing"
            @click="startBatchPrint"
          >
            <span
              v-if="printing"
              class="spinner-border spinner-border-sm mr-1"
            ></span>
            {{ printing ? "Preparing…" : "Print all" }}
          </button>
          <button type="button" class="btn btn-light" @click="goBack">
            Back to DTR
          </button>

          <ul class="employee-queue mt-4 mb-0">
            <li
              v-for="(employee, index) in batchData"
              :key="employee.BiometricID"
              :class="{ done: index < currentIndex, current: index === currentIndex && printing }"
            >
              {{ employee.LastName }}, {{ employee.FirstName }}
              <small class="text-muted">({{ employee.BiometricID }})</small>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div aria-hidden="true" class="dtr-compiler-host">
      <DTRCSformat
        v-if="compilingEmployee"
        ref="compiler"
        :key="compilingEmployee.BiometricID"
        :embed-personnel-id="compilingEmployee.BiometricID"
        :embed-from="from"
        :embed-to="to"
        embed-only
        :initial-user="compilingEmployee.user"
        :initial-logs="compilingEmployee.logs"
        :initial-leaves="compilingEmployee.leaves"
      />
    </div>

    <div id="bulk-dtr" class="bulk-dtr-print-root" aria-hidden="true"></div>
  </div>
</template>

<script>
import DTRCSformat from "./DTRCSformat.vue";

export default {
  name: "DTRBulkPrint",
  components: {
    DTRCSformat,
  },
  data() {
    return {
      batchData: [],
      loading: false,
      printing: false,
      currentIndex: 0,
      statusMessage: "",
      compilingEmployee: null,
    };
  },
  computed: {
    employeeType() {
      return this.$route.query.type || "COS";
    },
    from() {
      return this.$route.query.from || "";
    },
    to() {
      return this.$route.query.to || "";
    },
    typeLabel() {
      return this.employeeType === "Regular"
        ? "Regular employees"
        : "Contract of Service";
    },
  },
  mounted() {
    this.removeStalePrintStyles();
    if (!this.from || !this.to) {
      toastr.error("Select a date range on the DTR page first.", "Error");
      this.goBack();
      return;
    }
    this.loadBatchData();
  },
  methods: {
    removeStalePrintStyles() {
      const stale = document.getElementById("dtr-bulk-print-styles");
      if (stale) {
        stale.remove();
      }
      document.body.classList.remove("bulk-dtr-print-active");
    },
    goBack() {
      this.$router.push({ name: "dtr" });
    },
    loadBatchData() {
      this.loading = true;
      axios
        .get("api/dtr-batch-data", {
          params: {
            role: this.employeeType,
            from: this.from,
            to: this.to,
          },
        })
        .then((res) => {
          this.batchData = res.data || [];
        })
        .catch(() => {
          toastr.error("Failed to load employees.", "Error");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    waitForRender() {
      return new Promise((resolve) => {
        this.$nextTick(() => {
          window.setTimeout(resolve, 450);
        });
      });
    },
    async startBatchPrint() {
      if (this.printing || !this.batchData.length) {
        return;
      }

      if (!this.$htmlToPaper) {
        toastr.error("Print helper is not available.", "Error");
        return;
      }

      this.printing = true;
      this.currentIndex = 0;

      const container = document.getElementById("bulk-dtr");
      if (container) {
        container.innerHTML = "";
      }

      for (let i = 0; i < this.batchData.length; i += 1) {
        this.currentIndex = i;
        const employee = this.batchData[i];
        this.statusMessage = `Preparing ${i + 1} of ${this.batchData.length}: ${employee.FirstName} ${employee.LastName}`;
        this.compilingEmployee = employee;
        await this.waitForRender();

        const compiler = this.$refs.compiler;
        const dtrEl = compiler && compiler.$el.querySelector("#dtr-compiler");

        if (dtrEl && container) {
          const page = document.createElement("div");
          page.className = "dtr-print-page";
          const sheet = dtrEl.cloneNode(true);
          sheet.removeAttribute("id");
          page.appendChild(sheet);
          container.appendChild(page);
        }
      }

      this.compilingEmployee = null;
      this.currentIndex = this.batchData.length;
      this.statusMessage = "Opening print dialog…";
      await this.$nextTick();

      // Same print path as individual DTR: Bootstrap + DTR print CSS in one dialog.
      this.$htmlToPaper("bulk-dtr");

      this.printing = false;
      this.statusMessage = "Print dialog opened.";
      toastr.success("All DTR sheets compiled into one print job.", "Information");
    },
  },
};
</script>

<style scoped>
.bulk-dtr-page {
  max-width: 720px;
  margin: 0 auto;
}

.bulk-dtr-print-root {
  position: fixed;
  left: -9999px;
  top: 0;
  width: 992px;
  pointer-events: none;
  visibility: hidden;
}

.dtr-compiler-host {
  position: fixed;
  left: -9999px;
  top: 0;
  width: 992px;
  pointer-events: none;
  visibility: hidden;
  overflow: hidden;
}

.employee-queue {
  list-style: none;
  padding: 0;
  max-height: 360px;
  overflow-y: auto;
}

.employee-queue li {
  padding: 0.55rem 0.75rem;
  border-bottom: 1px solid rgba(0, 0, 21, 0.06);
}

.employee-queue li.done {
  color: #768192;
}

.employee-queue li.current {
  background: rgba(77, 141, 255, 0.08);
  font-weight: 600;
}
</style>
